import type { Offer, RefLine, RefPrestation, Enterprise } from '../types';

// ------------------ Types ------------------

export type AlertLevel = 'red' | 'orange';

export type AlertKind =
  // RED — irrégularités graves
  | 'line-total-mismatch'   // qty × pu ≠ total annoncé
  | 'offer-total-mismatch'  // total offre annoncé ≠ Σ(qty × pu)
  | 'oab'                   // offre anormalement basse
  | 'missing'               // ligne manquante (offer null)
  | 'unpriced'              // total = 0, "HORS LOT", "NON COMPRIS", "PM", "POUR MÉMOIRE", "NON CHIFFRÉ"
  // ORANGE — variances à vérifier
  | 'orphan'                // ligne ajoutée (orphan)
  | 'qty-diff'              // qty offerte ≠ qty réf
  | 'unit-mismatch'         // unité offerte ≠ unité réf
  | 'ecart-important'       // écart PU/total parmi les top-N de l'offre
  | 'offer-outlier'         // PU s'écarte fortement de la médiane inter-offres
  | 'match-uncertain';      // matching incertain

export interface AlertReason {
  kind: AlertKind;
  level: AlertLevel;
  message: string;
  /** Impact : utilisé pour trier les écarts orange en top-N par offre. */
  impact: number;
}

// ------------------ Détecteurs ------------------

// Régex "non chiffré" : HORS LOT, NON COMPRIS, NON CHIFFRÉ, PM, POUR MÉMOIRE, À LA CHARGE.
const UNPRICED_RE = /\b(hors\s*lot|non\s*compris|non\s*chiffr|pour\s*m[ée]moire|à\s*la\s*charge)\b|^\s*pm\s*$/i;

export function isUnpriced(offer: Offer): boolean {
  if (offer.total === 0 && offer.qty > 0) return true;
  return UNPRICED_RE.test(offer.designation);
}

export function lineTotalDelta(offer: Offer): number {
  return offer.total - offer.qty * offer.pu;
}

export function hasLineTotalMismatch(offer: Offer): boolean {
  return Math.abs(lineTotalDelta(offer)) > 0.5;
}

// Seuil minimal pour qu'un écart soit candidat au top-N : évite de classer les ± 2%.
const ECART_MIN_PCT = 5;

export function impactOf(offer: Offer, refLine: RefPrestation): number {
  const pct = Math.abs(offer.ecart ?? 0);
  const refTotal = refLine.refQty * refLine.refPU;
  const absEcart = Math.abs(offer.total - refTotal);
  // On pondère % et euros normalisés pour avoir un score comparable.
  return Math.max(pct, refTotal > 0 ? (absEcart / refTotal) * 100 : 0);
}

// ------------------ Inter-offer outlier ------------------

// Seuil PU à ±X% de la médiane inter-offres → flag orange. Plus large que l'écart vs réf (5%)
// car la dispersion naturelle entre soumissionnaires est plus élevée.
const OUTLIER_MIN_PCT = 20;

export function median(nums: number[]): number {
  if (nums.length === 0) return 0;
  const s = nums.slice().sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid];
}

// lineId -> entName -> deviation % vs médiane PU inter-offres
export function computeOutlierFlags(
  lines: RefLine[],
  optionalIds: Set<string>,
): Map<string, Map<string, number>> {
  const out = new Map<string, Map<string, number>>();
  for (const line of lines) {
    if (line.isSection) continue;
    if (optionalIds.has(line.id)) continue;
    const pus: Array<{ name: string; pu: number }> = [];
    for (const name of Object.keys(line.offers)) {
      const off = line.offers[name];
      if (off && off.pu > 0 && !isUnpriced(off)) pus.push({ name, pu: off.pu });
    }
    if (pus.length < 3) continue;
    const med = median(pus.map(p => p.pu));
    if (med <= 0) continue;
    const perLine = new Map<string, number>();
    for (const { name, pu } of pus) {
      const devPct = ((pu - med) / med) * 100;
      if (Math.abs(devPct) >= OUTLIER_MIN_PCT) perLine.set(name, devPct);
    }
    if (perLine.size > 0) out.set(line.id, perLine);
  }
  return out;
}

// ------------------ Unit mismatch ------------------

// Normalisation tolérante : casse, espaces, caractères typographiques (m³ ≡ m3, m² ≡ m2).
// Ne cherche pas à mapper les équivalences métier (ens ≢ u) — ce sont justement les vrais mismatches.
function normalizeUnit(u: string): string {
  return u.trim().toLowerCase().replace(/\s+/g, '').replace(/³/g, '3').replace(/²/g, '2').replace(/µ/g, 'u');
}

export function isUnitMismatch(offerUnit: string | undefined, refUnit: string): boolean {
  if (!offerUnit || !refUnit) return false;
  return normalizeUnit(offerUnit) !== normalizeUnit(refUnit);
}

// Renvoie, pour chaque offre, l'ensemble des lineIds retenus en "écart important" (top-N par impact).
export function computeEcartFlags(
  enterprises: Enterprise[],
  lines: RefLine[],
  optionalIds: Set<string>,
  topN: number,
): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const ent of enterprises) {
    const candidates: Array<{ id: string; impact: number }> = [];
    for (const line of lines) {
      if (line.isSection) continue;
      if (optionalIds.has(line.id)) continue;
      const offer = line.offers[ent.name];
      if (!offer) continue;
      if (Math.abs(offer.ecart ?? 0) < ECART_MIN_PCT) continue;
      candidates.push({ id: line.id, impact: impactOf(offer, line) });
    }
    candidates.sort((a, b) => b.impact - a.impact);
    out.set(ent.name, new Set(candidates.slice(0, topN).map(c => c.id)));
  }
  return out;
}

// ------------------ Cell-level agrégé ------------------

export interface CellAlertContext {
  oabEnts: Set<string>;
  ecartFlagged: Map<string, Set<string>>; // entName -> Set<lineId>
  outlierFlagged?: Map<string, Map<string, number>>; // lineId -> entName -> deviation % vs médiane
  isOptional: boolean;
}

export function computeCellAlerts(
  line: RefPrestation,
  entName: string,
  offer: Offer | null,
  ctx: CellAlertContext,
): { level: AlertLevel | null; reasons: AlertReason[] } {
  if (ctx.isOptional) return { level: null, reasons: [] };
  const reasons: AlertReason[] = [];

  if (!offer) {
    reasons.push({ kind: 'missing', level: 'red', impact: 100, message: 'Ligne non matchée' });
    return { level: 'red', reasons };
  }

  // RED
  if (isUnpriced(offer)) {
    reasons.push({ kind: 'unpriced', level: 'red', impact: 90, message: 'Ligne non chiffrée' });
  }
  if (hasLineTotalMismatch(offer)) {
    const delta = lineTotalDelta(offer);
    reasons.push({
      kind: 'line-total-mismatch',
      level: 'red',
      impact: 80,
      message: `Total ${offer.total.toLocaleString('fr-FR')} € ≠ ${(offer.qty * offer.pu).toLocaleString('fr-FR')} € (${delta > 0 ? '+' : ''}${delta.toLocaleString('fr-FR')} €)`,
    });
  }

  // ORANGE
  if (offer.status === 'suspect') {
    reasons.push({ kind: 'match-uncertain', level: 'orange', impact: 40, message: 'Matching incertain' });
  }
  if (offer.qty !== line.refQty && line.refQty > 0) {
    const diffPct = ((offer.qty - line.refQty) / line.refQty) * 100;
    reasons.push({
      kind: 'qty-diff',
      level: 'orange',
      impact: Math.abs(diffPct),
      message: `Qté ${offer.qty} au lieu de ${line.refQty} (${diffPct > 0 ? '+' : ''}${diffPct.toFixed(1)}%)`,
    });
  }
  if (isUnitMismatch(offer.unit, line.refUnit)) {
    reasons.push({
      kind: 'unit-mismatch',
      level: 'orange',
      impact: 50,
      message: `Unité ${offer.unit} au lieu de ${line.refUnit} (réf)`,
    });
  }
  const outlierDev = ctx.outlierFlagged?.get(line.id)?.get(entName);
  if (outlierDev !== undefined) {
    reasons.push({
      kind: 'offer-outlier',
      level: 'orange',
      impact: Math.abs(outlierDev),
      message: `PU ${outlierDev > 0 ? '+' : ''}${outlierDev.toFixed(1)}% vs médiane inter-offres`,
    });
  }
  if (ctx.ecartFlagged.get(entName)?.has(line.id)) {
    const pct = offer.ecart ?? 0;
    reasons.push({
      kind: 'ecart-important',
      level: 'orange',
      impact: Math.abs(pct),
      message: `Écart ${pct > 0 ? '+' : ''}${pct.toFixed(1)}% vs référence`,
    });
  }

  const level: AlertLevel | null = reasons.some(r => r.level === 'red')
    ? 'red'
    : reasons.some(r => r.level === 'orange')
      ? 'orange'
      : null;
  return { level, reasons };
}
